import React, { useCallback, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import GenericUpper from "../../components/generic-upper";
import { Container } from "react-grid-system";
import ModalHistoricoExportar from "../../components/modal/modalHistoricoExportar";
import TableWithLabelBlackV2 from '../../components/TableWithLabelBlack/indexV2';
import HomologButtonHistory from "../../components/historico/homologButtonHistory";
import ExportarDefesasNfDetails from "./details-nf";
import { Loading } from "../../components/loading";
import ArrowDown from "../../assets/icons/icon-arrow-down.svg";
import status0 from "../../assets/icons/icon-status-nao-enviado.png";
import status2 from "../../assets/icons/icon-status-pending.png";
import status3 from "../../assets/icons/icon-status-enviado.svg";
import status4 from "../../assets/icons/icon-status-reload.png";
import { TaglineP } from "../../styles/stylesTypography";
import { theme } from "../../styles/theme";
import api from "../../services/api";
import queryString from "query-string";
import * as S from "./styles";

const OPTION_VALUE_BY_UF = {
  PR: 1,
  RS: 5,
  BA: 7,
  MG: 8,
};

const LABEL_MAP = {
  PR: "ART → SIAGRO",
  RS: "ART → SDA RS",
  BA: "ART → ADAB",
  MG: "ART → SICCA",
};

const LABEL_MAP_NF = {
  PR: "NF → SIAGRO",
  RS: "NF → SDA RS",
  BA: "NF → ADAB",
  MG: "NF → SICCA",
};

// Mesma lógica de status/coluna já usada em details.js (GO/DF/ES/MT/SC) -
// reaproveitada aqui pois esse relatório também lista receitas, não ARTs.
// Para BA/MG/PR/RS cai sempre no fallback genérico ("HOMOLOGAÇÃO" e os 3
// status comuns), já que essas UFs não têm as particularidades do SISDEV/MT.
const FILTER_FIELD_MAP = {
  numero: "numero",
  protocolo: "protocolo",
  produtor: "produtor",
  propriedade: "propriedade",
  data_emissao: "data_emissao",
  cultura: "cultura",
  produtos: "produtos",
  status: "status",
};

const normalizeFilterValue = (value) => String(value ?? "").trim();
const isEmptyMode = (mode) => mode === "isEmpty" || mode === "isNotEmpty";

const buildConstraint = (matchMode, value) => {
  const normalizedValue = normalizeFilterValue(value);
  if (!isEmptyMode(matchMode) && !normalizedValue) return null;
  return { matchMode, value: normalizedValue };
};

const buildApiFiltersPayload = (columnFilters = {}) => {
  return Object.entries(columnFilters).reduce((acc, [columnKey, state]) => {
    const apiField = FILTER_FIELD_MAP[columnKey];
    if (!apiField || !state) return acc;

    if (columnKey === "status") {
      const selectedValues = Array.isArray(state.selectedValues)
        ? state.selectedValues.map((value) => normalizeFilterValue(value)).filter(Boolean)
        : [];
      if (selectedValues.length === 0) return acc;

      acc[apiField] = {
        operator: selectedValues.length > 1 ? "or" : "and",
        constraints: selectedValues.map((value) => ({ matchMode: "contains", value })),
      };
      return acc;
    }

    const constraints = [];
    const sharedValueForEmptyMode = isEmptyMode(state.mode)
      ? (state.secondaryValue ?? state.value)
      : state.value;
    const primaryConstraint = buildConstraint(state.mode, sharedValueForEmptyMode);
    if (primaryConstraint) constraints.push(primaryConstraint);

    const secondaryConstraint = buildConstraint(state.secondaryMode, state.secondaryValue);
    if (secondaryConstraint) constraints.push(secondaryConstraint);
    if (constraints.length === 0) return acc;

    acc[apiField] = {
      operator: state.combine === "or" ? "or" : "and",
      constraints,
    };
    return acc;
  }, {});
};


// O texto exibido de verdade na coluna Status/Ação vem do HomologButtonHistory,
// que usa um vocabulário PRÓPRIO por UF (não é genérico "Enviado/Enviando/Não
// enviado para {sistema}" como nos estados com webservice de homologação).
// As opções abaixo espelham exatamente esse vocabulário para não mostrar no
// filtro um status que nunca aparece na coluna.
const getStatusFilterOptionsByUf = (estado) => {
  const normalized = String(estado || "").toUpperCase();

  if (normalized === "BA") {
    return [
      { label: "NF Anexada", value: "0", color: theme.colors.green.Folha, backgroundColor: "#2FBB6914", padding: "5px 8px", icon: status3 },
      { label: "NF Pendente", value: "3", color: "#ff8d14", backgroundColor: "#F89E4B14", padding: "5px 8px", icon: status4 },
    ];
  }

  if (normalized === "MG") {
    return [
      { label: "NF Anexada", value: "1", color: theme.colors.green.Folha, backgroundColor: "#2FBB6914", padding: "5px 8px", icon: status3 },
      { label: "NF Pendente", value: "3", color: "#ff8d14", backgroundColor: "#F89E4B14", padding: "5px 8px", icon: status4 },
    ];
  }

  if (normalized === "RS") {
    return [
      { label: "Enviado para RS", value: "1", color: theme.colors.green.Folha, backgroundColor: "#2FBB6914", padding: "5px 8px", icon: status3 },
      { label: "Enviando para RS", value: "2", color: theme.colors.yellow.Gema, backgroundColor: "#F8C84B1F", padding: "5px 8px", icon: status2 },
      { label: "Não enviado para RS", value: "0", color: theme.colors.red.Tostado, backgroundColor: "#FF525214", padding: "5px 8px", icon: status0 },
    ];
  }

  // PR (e qualquer outra UF que caia nessa rota genérica): não há
  // homologação estadual nem exigência de NF - o status reflete apenas o
  // ciclo de vida da própria receita (emitida ou cancelada).
  return [
    { label: "Emitida", value: "1", color: theme.colors.green.Folha, backgroundColor: "#2FBB6914", padding: "5px 8px", icon: status3 },
    { label: "Cancelada", value: "0", color: theme.colors.red.Tostado, backgroundColor: "#FF525214", padding: "5px 8px", icon: status0 },
  ];
};

const getStatusLabelForFilter = (row, estado) => {
  const normalized = String(estado || "").toUpperCase();
  const homologado = Number(row?.homologado);

  if (normalized === "BA") {
    if (homologado === 0) return "NF ANEXADA";
    if (homologado === 3) return "NF PENDENTE";
    return "";
  }

  if (normalized === "MG") {
    if (homologado === 1) return "NF ANEXADA";
    // MG tem receitas legadas criadas antes de MG passar a nascer com
    // homologado 3 - ficaram com homologado=0 mas representam a mesma "NF
    // pendente" (ver homologButtonHistory.js e o groupBy de contagem em
    // relatorios.service.ts), por isso soma os dois aqui também.
    if (homologado === 3 || homologado === 0) return "NF PENDENTE";
    return "";
  }

  if (normalized === "RS") {
    if (homologado === 0) return "Não enviado para RS";
    if (homologado === 1) return "Enviado para RS";
    if (homologado === 2) return "Enviando para RS";
    return "";
  }

  const status = Number(row?.status);
  if (status === 1) return "EMITIDA";
  if (status === 0) return "CANCELADA";
  return "";
};

const getInvoiceNumberLabel = (row) => {
  const invoices = row?.agro_prescription_invoices;
  if (!Array.isArray(invoices) || invoices.length === 0) return "-";

  const values = invoices
    .map((invoice) => String(invoice?.nf ?? "").trim())
    .filter(Boolean);

  return values.length > 0 ? values.join(", ") : "-";
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const ExportarDefesasRelatorioDetails = ({ match, history, location }) => {
  const { uf } = match.params;
  const isNf = match.path.startsWith("/exportar-defesas/relatorio/nf");
  const optionValue = OPTION_VALUE_BY_UF[uf];
  const query = queryString.parse(location.search);
  const siagroLink = uf === "PR" && query.siagroLink === "2" ? 2 : 1;

  const label = uf === "PR" && siagroLink === 2
    ? (isNf ? "NF → SIAGRO" : "ENVIO NF → SIAGRO")
    : (isNf ? LABEL_MAP_NF : LABEL_MAP)[uf] || uf;

  const goBackToList = () => history.push("/exportar-defesas");

  const [receitas, setReceitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(30);
  const [total, setTotal] = useState(0);
  const [columnFilters, setColumnFilters] = useState({});
  const [visibleRowsCount, setVisibleRowsCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const lastFilterKeyRef = useRef("");
  const requestSeqRef = useRef(0);

  const fetchReceitas = useCallback(async (currentLimit = limit, options = {}) => {
    if (isNf) return;
    const { showLoader = true } = options;
    const requestId = ++requestSeqRef.current;
    if (showLoader) setLoading(true);
    setTableLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("@userinfo"));
      const subsidiaryId = user?.agro_subsidiary_id;

      if (subsidiaryId && uf) {
        const response = await api.post(
          `/relatorios/homologados/${subsidiaryId}/${uf}?limit=${currentLimit}&offset=0`,
          { filters: buildApiFiltersPayload(columnFilters) }
        );
        if (requestId !== requestSeqRef.current) return;
        const data = response?.data?.data || [];
        const totalCount = response?.data?.total || 0;
        const normalizedData = Array.isArray(data) ? data : [];
        setReceitas(normalizedData);
        setVisibleRowsCount(normalizedData.length);
        setTotal(totalCount);
      }
    } catch (e) {
      if (requestId !== requestSeqRef.current) return;
      setReceitas([]);
    } finally {
      if (requestId === requestSeqRef.current) {
        setTableLoading(false);
        if (showLoader) setLoading(false);
      }
    }
  }, [uf, isNf, columnFilters, limit]);

  React.useEffect(() => {
    fetchReceitas(limit, { showLoader: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uf]);

  const handleShowMore = () => {
    const newLimit = limit + 30;
    setLimit(newLimit);
    fetchReceitas(newLimit, { showLoader: true });
  };

  const handleColumnFiltersChange = useCallback((nextFilters) => {
    setColumnFilters((previousFilters) => {
      const previousKey = JSON.stringify(previousFilters ?? {});
      const nextKey = JSON.stringify(nextFilters ?? {});
      return previousKey === nextKey ? previousFilters : (nextFilters ?? {});
    });
  }, []);

  React.useEffect(() => {
    const nextKey = JSON.stringify(columnFilters || {});
    const previousKey = lastFilterKeyRef.current;
    if (nextKey === previousKey) return;
    lastFilterKeyRef.current = nextKey;
    const hasActiveFiltersNow = nextKey !== "{}" && nextKey !== "";
    const hadActiveFiltersBefore = previousKey !== "" && previousKey !== "{}";
    if (!hasActiveFiltersNow && !hadActiveFiltersBefore) return;

    const timer = setTimeout(() => {
      setLimit(30);
      fetchReceitas(30, { showLoader: false });
    }, 2000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const statusFilterOptions = getStatusFilterOptionsByUf(uf);

  // PR e RS não têm homologação estadual real rastreada por receita (o
  // envio pra essas UFs é feito via relatório em lote, fora do app) - a
  // coluna de status/ação não tem o que mostrar de confiável pra elas.
  const showStatusColumn = uf !== "PR" && uf !== "RS";

  const columns = [
    ...(showStatusColumn
      ? [
          {
            label: "Status / Ação",
            key: "status",
            width: "220px",
            minWidth: "130px",
            enableHorizontalScroll: true,
            justifyContent: "flex-start",
            filterType: "checkbox",
            filterOptions: statusFilterOptions,
            filterBy: (row) => getStatusLabelForFilter(row, uf),
            render: (row) => (
              <HomologButtonHistory
                style={{ width: "auto" }}
                estado={uf}
                receitaId={row.id}
                statusEnvio={parseInt(row.status_homologacao)}
                updateReceitas={fetchReceitas}
                propsLocation={{ match, history, location }}
                homologado={row.homologado}
                statusReceita={row.status}
                homologacaoErro={row.homologacao_erro}
                dataReceita={row}
              />
            ),
          },
        ]
      : []),
    {
      label: "Nº Receita",
      key: "numero",
      minWidth: "120px",
      filterBy: (row) => (row.numero != null ? String(row.numero) : ""),
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_1}>{row.numero != null ? String(row.numero) : "-"}</TaglineP>
      ),
    },
    {
      label: "Nº Protocolo",
      key: "protocolo",
      minWidth: "120px",
      filterBy: (row) => row.nu_receita || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_1}>{row.nu_receita || "-"}</TaglineP>
      ),
    },
    {
      label: "Produtor",
      key: "produtor",
      width: "100%",
      minWidth: "280px",
      maxWidth: "280px",
      enableHorizontalScroll: true,
      filterBy: (row) => row.produtor_nome || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_1}>{row.produtor_nome || "-"}</TaglineP>
      ),
    },
    {
      label: "Propriedade",
      key: "propriedade",
      width: "100%",
      minWidth: "220px",
      maxWidth: "220px",
      enableHorizontalScroll: true,
      filterBy: (row) => row.local_aplicacao_nome || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_1}>{row.local_aplicacao_nome || "-"}</TaglineP>
      ),
    },
    {
      label: "Data Emissão",
      key: "data_emissao",
      minWidth: "140px",
      enableFilter: false,
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_1}>{formatDate(row.data_emissao)}</TaglineP>
      ),
    },
    {
      label: "Cultura",
      key: "cultura",
      width: "100%",
      minWidth: "120px",
      maxWidth: "130px",
      enableHorizontalScroll: true,
      filterBy: (row) => row.cultura_nome_comum || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_1}>{row.cultura_nome_comum || "-"}</TaglineP>
      ),
    },
    {
      label: "Produtos",
      key: "produtos",
      width: "100%",
      minWidth: "200px",
      maxWidth: "220px",
      enableHorizontalScroll: true,
      filterBy: (row) => {
        const relations = row.agro_prescriptions_relations || [];
        return relations.map((rel) => rel?.nome_comercial || "").filter(Boolean).join(" | ");
      },
      render: (row) => {
        const relations = row.agro_prescriptions_relations || [];
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {relations.length > 0 ? (
              relations.map((rel, idx) => (
                <TaglineP key={idx} color={theme.colors.gray.Cinza_1}>
                  {rel.nome_comercial || "-"}
                </TaglineP>
              ))
            ) : (
              <TaglineP color={theme.colors.gray.Cinza_1}>-</TaglineP>
            )}
          </div>
        );
      },
    },
    {
      label: "Número NF",
      key: "numero_nf",
      width: "100%",
      minWidth: "120px",
      maxWidth: "120px",
      enableHorizontalScroll: true,
      enableFilter: false,
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_1}>{getInvoiceNumberLabel(row)}</TaglineP>
      ),
    },
  ];

  if (isNf) {
    // O relatório de NF tem colunas e origem de dados totalmente diferentes
    // do relatório de receitas abaixo (status de envio, nº NF, cliente,
    // produtos etc.), então reaproveita a implementação já existente e
    // correta usada hoje para GO/DF/ES/MT/SC.
    return <ExportarDefesasNfDetails match={match} history={history} location={location} />;
  }

  return (
    <>
      <GenericUpper title={`Exportar Defesas - ${uf}`} backLink="/exportar-defesas" />
      <Container style={{ paddingLeft: "32px", paddingRight: "32px" }}>
        <div style={{ maxWidth: "820px", margin: "16px auto 24px" }}>
          {optionValue ? (
            <ModalHistoricoExportar
              asPage
              show={true}
              title=""
              option={`${uf}: ${label}`}
              optionValue={optionValue}
              siagroLink={siagroLink}
              tipo={isNf ? "nf" : "art"}
              update={goBackToList}
              closeModal={goBackToList}
              setFilter={{}}
              resposta=""
            />
          ) : null}
        </div>

        {loading && receitas.length === 0 ? (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
            <Loading size="64px" />
          </div>
        ) : (
          <>
            <div style={{ position: "relative", minHeight: visibleRowsCount === 0 ? "170px" : "auto" }}>
              <TableWithLabelBlackV2
                columns={columns}
                data={receitas}
                showAll={true}
                itemsPerPage={100}
                serverSide={true}
                isLoading={tableLoading}
                enableColumnFilters={true}
                useServerSideColumnFilters={true}
                onColumnFiltersChange={handleColumnFiltersChange}
                enableDefaultRowNavigation={false}
                emptyMessage="Não há resultados"
                getRowId={(row) => row?.id}
                onCountChange={(_, visibleCount) => setVisibleRowsCount(visibleCount)}
              />
            </div>
            {receitas.length < total && (
              <S.ButtonShowAll onClick={handleShowMore}>
                <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={800}>
                  Ver mais
                </TaglineP>
                <img src={ArrowDown} alt="Ícone de flecha direcionada para baixo" />
              </S.ButtonShowAll>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default withRouter(ExportarDefesasRelatorioDetails);
